import React from "react";
import { Close, DoneAll } from "@mui/icons-material";

const MessageSeenByModal = ({ isOpen, onClose, message, chat, currentUserId }) => {
  if (!isOpen || !message) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  // Get users who have seen the message (excluding current user)
  const seenByUsers = (message.readBy || [])
    .map((readUser) => {
      const userId = typeof readUser === "string" ? readUser : readUser._id;
      const user = chat?.users?.find((u) => {
        const uId = u._id || u.id;
        return uId === userId;
      });
      return user;
    })
    .filter((user) => user && user._id !== currentUserId);

  // Get users who have NOT seen the message yet (excluding current user)
  const notSeenUsers = (chat?.users || []).filter((user) => {
    const userId = user._id || user.id;
    if (userId === currentUserId) return false;
    
    const hasRead = (message.readBy || []).some((readUser) => {
      const readUserId = typeof readUser === "string" ? readUser : readUser._id;
      return readUserId === userId;
    });
    
    return !hasRead;
  });

  const getProfilePicUrl = (user) => {
    if (user?.profilePic) {
      const baseUrl = user.profilePic.startsWith("http")
        ? user.profilePic
        : `${BASE_DOWNLOAD_URL}${user.profilePic}`;
      return `${baseUrl}?v=${user.updatedAt || Date.now()}`;
    }
    return null;
  };

  const getInitials = (user) => {
    if (user?.firstName || user?.lastName) {
      return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
    }
    return user?.username?.[0]?.toUpperCase() || "U";
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-96 max-w-[90vw] max-h-[80vh] mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-800">Message Info</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Close className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Seen By Section */}
          {seenByUsers.length > 0 && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <DoneAll className="w-5 h-5 text-blue-500" />
                <h4 className="text-sm font-semibold text-gray-700">
                  Read by {seenByUsers.length}
                </h4>
              </div>
              <div className="space-y-2">
                {seenByUsers.map((user) => {
                  const profilePicUrl = getProfilePicUrl(user);
                  const readByEntry = (message.readBy || []).find((r) => {
                    const rId = typeof r === "string" ? r : r._id;
                    return rId === user._id;
                  });
                  const readAt = typeof readByEntry === "object" ? readByEntry.readAt : null;

                  return (
                    <div
                      key={user._id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="relative w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-700 overflow-hidden flex-shrink-0">
                        {profilePicUrl ? (
                          <img
                            src={profilePicUrl}
                            alt={user.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          getInitials(user)
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.username || "Unknown"}
                        </div>
                        {readAt && (
                          <div className="text-xs text-gray-500">
                            {formatTime(readAt)}
                          </div>
                        )}
                      </div>
                      <DoneAll className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Delivered To / Not Seen Section */}
          {notSeenUsers.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <DoneAll className="w-5 h-5 text-gray-400" />
                <h4 className="text-sm font-semibold text-gray-700">
                  Delivered to {notSeenUsers.length}
                </h4>
              </div>
              <div className="space-y-2">
                {notSeenUsers.map((user) => {
                  const profilePicUrl = getProfilePicUrl(user);

                  return (
                    <div
                      key={user._id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="relative w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-700 overflow-hidden flex-shrink-0">
                        {profilePicUrl ? (
                          <img
                            src={profilePicUrl}
                            alt={user.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          getInitials(user)
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.username || "Unknown"}
                        </div>
                      </div>
                      <DoneAll className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {seenByUsers.length === 0 && notSeenUsers.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <DoneAll className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No read receipts available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 text-center">
            {message.createdAt && (
              <div>
                Sent: {new Date(message.createdAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageSeenByModal;